import {useEffect, useMemo, useState} from 'react';

import {Box, Divider, Typography} from '@mui/material';
import * as sentry from '@sentry/react';
import {useTranslation} from 'react-i18next';

import {ResponsiveAlertPrompts} from 'components/alert-prompts/ResponsiveAlertPrompts';
import {config} from 'config/env';
import {isEnabledRecommendationMFE} from 'constants/recommendation';
import useAnalytics, {ANALYTICS, EVENT_SOURCE, SUGGESTEDLIST_CHANNELS} from 'contexts/analytics-context';
import {useAppDispatch, useAppSelector} from 'hooks';
import {useAppliedOmniChannelVoucher} from 'hooks/useAppliedOmniChannelVoucher';
import {useAppMode} from 'hooks/useAppMode';
import {useExpressDimensionLimit} from 'hooks/useExpressDimensionLimit';
import {useExpressWeightLimit} from 'hooks/useExpressWeightLimit';
import {useOptimizedDependency} from 'hooks/useOptimizedDependency';
import {useSelectedItemsLocalStorage} from 'hooks/useSelectedItemsLocalStorage';
import {applyPromotionVoucher, updateBasketStoreCodeREST} from 'services/basket/basket-service';
import {createPartialCheckoutV3} from 'services/checkout/checkout-service';
import MultiStoreService from 'services/multistore/multistore-service';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {updateBasketAddressAction} from 'store/actions/addresses-action';
import {setAlertAction} from 'store/actions/alert-action';
import {
  setAppModeAction,
  setCurrentStoreWithLocationLocalesAction,
  setNeedRetriggerCheckoutAction
} from 'store/actions/app-action';
import {fetchUserDetailsAction} from 'store/actions/auth-action';
import {
  batchUpdateItemsQuantityAction,
  checkedUnselectItemIdsAction,
  deleteSelectedItemsAction,
  fetchBasketVouchersAction,
  getBasketCalculationAction,
  getBasketRecommendationsAction,
  getRM3PromotionsAction,
  removeUnavailableItemsAction,
  selectItemIdsAction,
  setBasketQuantityChangedProductMapAction,
  setSelectedItemsAction,
  setVariantIdsOfItemThatWasAdjustedQuantityAction,
  updateMetaDataAction
} from 'store/actions/basket-action';
import {fetchUserPointsAction} from 'store/actions/campaign-action';
import {AffiliateProgramType, AlertType} from 'store/types/app';
import {
  BasketErrorTypes,
  BasketItemInput,
  BasketLineItem,
  BasketState,
  ExpressTimeFrame,
  IBasketQuantityChangedProductMap,
  ItemToUpdateQuantity,
  TPinMapSelectedLocation,
  UserAddress
} from 'types';
import {FetchBasketVouchersQuery} from 'types/campaign';
import {ProductUnavailableReasonCodes} from 'types/recommedation-types/recommendations';
import {
  createCheckoutPayload,
  DeviceType,
  filterUnavailableProducts,
  getAddressStringForGeocoding,
  getAlcoholProducts,
  getAlcoholVolumeInLiters,
  getDeviceType,
  getIdsFromBasketItems,
  getProductAvailability,
  hasItemsInBasket,
  is1PMakroStore,
  isDropShip,
  isFeatureEnabled,
  isTimeEligibleForExpress,
  mapUserAddressToShippingAddress,
  sanitizeAddress
} from 'utils';
import {geocodeAddress, loadGoogleMapsApi} from 'utils/googleMapHelper';
import {filter1PItems, filter3PItems, isSyncAddressEnabled, isUnifiedBasketEnabled} from 'utils/helper';
import {AddressDrawer} from 'views/address-drawer/AddressDrawerContainer';
import AddressFormView from 'views/address-form/AddressFormView';
import {PinMapModal} from 'views/address-form/PinMapModal';
import Body from 'views/basket-body/Body';
import {Footer} from 'views/basket-footer/BasketFooterContainer';
import Header from 'views/basket-header/Header';
import BasketPopUpDialog from 'views/basket-modals/BasketPopUpDialog';
import BasketSummaryModal from 'views/basket-modals/BasketSummaryModal';
import FlexiBasketConfirmModal from 'views/basket-modals/FlexiBasketConfirmModal';
import ProductItem from 'views/basket-modals/ProductItem';

import {StyledBasket, StyledProductInfoWrapper, StyledSectionContentContainer, StyledSectionWrap} from './styles';
import BuildInfo from '../../components/build-info/BuildInfo';
import {ALCOHOL_VOLUME_THRESHOLD, AppMode} from '../../constants/index';

interface IBasektAffectedModalData {
  modalShown: boolean;
  quantityUpdatedItems: number;
  notReadyToPurchase: number;
}

const BasketWrapper = () => {
  const {t, i18n} = useTranslation();
  const dispatch = useAppDispatch();
  const selectedItemsLocalStorage = useSelectedItemsLocalStorage();
  const {isExpress, appMode} = useAppMode();
  const {isWithinThreshold: isWithinWeightThreshold, threshold: weightThreshold} = useExpressWeightLimit();
  const {isWithinThreshold: isWithinDimensionThreshold, threshold: dimensionThreshold} = useExpressDimensionLimit();
  const analytics = useAnalytics();

  const isUnifiedBasketEnabledFlag = isUnifiedBasketEnabled();

  const trueId = useAppSelector((state) => state.app.trueId);
  const mpid = useAppSelector((state) => state.app.mpid);
  const [openFullMapView, setOpenFullMapView] = useState<boolean>(false);
  const [isAddressFormModalOpen, setIsAddressFormModalOpen] = useState<boolean>(false);
  const [isPinMapViewOpen, setIsPinMapViewOpen] = useState<boolean>(false);
  const [addressBeingEdited, setAddressBeingEdited] = useState<UserAddress>();
  const [hasUserMovedMapManually, setHasUserMovedMapManually] = useState<boolean>(false);
  const [basketAffectedModalData, setBasketAffectedModalData] = useState<IBasektAffectedModalData>({
    modalShown: false,
    quantityUpdatedItems: 0,
    notReadyToPurchase: 0
  });
  const [isAddressDrawerOpen, setAddressDrawerOpen] = useState(false);
  const [checkoutInitiated, setCheckoutInitiated] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showBasketSummaryModal, setShowBasketSummaryModal] = useState(false);

  const [pinMapData, setPinMapData] = useState<TPinMapSelectedLocation | null>(null);

  const isAddressFlowRevampEnabled = true;
  const isThai = i18n.language === 'th';
  const isReactNativeWebView = ReactNative.isReactNativeWebView();
  const isOmniVoucherPlatformEnabled = isReactNativeWebView;

  const {
    omniChannelsVoucherRedemptionEnabled,
    basket,
    isLoading,
    isBasketApiFailed,
    basketQuantityChangedProductMap,
    selectedItems
  } = useAppSelector((state) => state?.basket);

  const {resetEffectValue, isFromBasketNotification, isStoreInActive} = useAppSelector((state) => state.app);

  useEffect(() => {
    setAddressDrawerOpen(false);
    setIsAddressFormModalOpen(false);
    setIsPinMapViewOpen(false);
    setHasUserMovedMapManually(false);
  }, [resetEffectValue]);

  const has1PSelected = useMemo<boolean>(() => {
    return !!basket?.basketItems
      ?.filter((item) => selectedItems?.includes(item.productId))
      .some((item) => !isDropShip(item.productDetails?.sellerDetails.marketplaceType));
  }, [basket?.basketItems, selectedItems]);

  const {
    currentStore,
    isDirectCheckout,
    defaultMembershipCard,
    pricezaClickId,
    accessTradeClickId,
    isAppLoading,
    appVersion,
    deviceOS
  } = useAppSelector((state) => state?.app);
  const {idToken, customerId} = useAppSelector((state) => state?.auth) || {};
  const hasIdToken = !!idToken; // just used for the useEffect dependency to prevent exhaustive dependency
  const isCurrentStoreLoaded = !!currentStore?.zipcode && !!currentStore?.subdistrict; // just used for the useEffect dependency to prevent exhaustive dependency

  const userAddressList = useAppSelector((state) => state?.addresses?.availableAddresses) || [];
  const shippingAddress = useAppSelector((state) => state?.addresses?.selectedShippingAddress);
  const needRetriggerCheckout = useAppSelector((state) => state.app.needRetriggerCheckout);
  const unavailableProducts = basket?.unavailableItems || [];
  const zipcodeForUpdateBasket = currentStore?.zipcode;
  const currentStoreCodes = currentStore?.storeCodes;
  const persistedSelectedItems: string[] = selectedItemsLocalStorage.get();
  const numberOfItems = persistedSelectedItems?.length || 0;

  const toggleAddressDrawer = () => setAddressDrawerOpen(!isAddressDrawerOpen);

  const minimumVersion = config.IS_COMPLEX_PROMOTION_ENABLED;

  const isComplexPromotionEnabled = isFeatureEnabled(minimumVersion, appVersion || '1.0.0');

  const isSyncAddress = isSyncAddressEnabled();
  const appliedOmniChannelVoucher = useAppliedOmniChannelVoucher();

  const basketProductIds = useOptimizedDependency(
    filter1PItems(basket?.basketItems || []).map((p) => ({
      id: p.productDetails.id,
      displayPrice: p.productDetails.displayPrice
    })) ?? [],
    (data) => {
      return data
        .sort((a, b) => a.id.localeCompare(b.id))
        .map((p) => `${p.id}-${p.displayPrice}`)
        .join(',');
    }
  );

  useEffect(() => {
    if (isComplexPromotionEnabled && basketProductIds) {
      const productIds = basketProductIds
        .map((p) => ({
          shopifyProductId: p.id || '',
          price: p.displayPrice?.toString() || '0'
        }))
        .filter((p) => p.shopifyProductId);

      if (productIds.length > 0) {
        dispatch(getRM3PromotionsAction({ids: productIds}));
      }
    }
  }, [basketProductIds, dispatch, isComplexPromotionEnabled]);

  const onShipToClickHandle = () => {
    const needToOpenAddressForm = !userAddressList?.length;
    if (idToken && needToOpenAddressForm) {
      _showPinMapModal();
    } else {
      toggleAddressDrawer();
    }
  };
  const hasProductInBasket = hasItemsInBasket(basket);
  const isDisabledCheckoutBtn = () => {
    const basketTotal = basket?.prices?.totalDisplayPricePostVoucher || 0;
    const noCurrentStore = !currentStore?.storeCodes?.length;
    // const isSelectedAddressIsIncomplete = !shippingAddress;

    return (
      (isExpress && !isWithinWeightThreshold) ||
      (isExpress && !isWithinDimensionThreshold) ||
      (isExpress &&
        !isTimeEligibleForExpress(
          isUnifiedBasketEnabledFlag
            ? (basket?.expressStoreInfo?.[0]?.expressDeliveryTimeFrame as ExpressTimeFrame)
            : (basket?.storeInfo?.[0]?.expressDeliveryTimeFrame as ExpressTimeFrame)
        )) ||
      // isSelectedAddressIsIncomplete ||
      !hasProductInBasket ||
      !basket?.basketItems?.length ||
      (basketTotal < (basket?.minimumSpend || 0) && !!has1PSelected) ||
      noCurrentStore
    );
  };

  const handleCheckout = async (validatorFunction?: (basketItem: BasketLineItem[]) => boolean): Promise<void> => {
    setCheckoutInitiated(true);
    if (isExpress && !isTimeEligibleForExpress(basket?.storeInfo?.[0]?.expressDeliveryTimeFrame as ExpressTimeFrame)) {
      SendBrowserEvent.showErrorToast(undefined, t('express.message.outOfOrderTime'));
      setCheckoutInitiated(false);
      return;
    }
    try {
      if (validatorFunction) {
        dispatch(getBasketCalculationAction());

        if (!validatorFunction(basket?.basketItems || [])) {
          setCheckoutInitiated(false);
          return;
        }
      }

      if ((!zipcodeForUpdateBasket || !currentStore?.subdistrict) && isAddressFlowRevampEnabled) {
        throw new Error(BasketErrorTypes.MISSING_ZIP_OR_SUBDISTRICT);
      }

      const customerDefaultAddress = shippingAddress
        ? mapUserAddressToShippingAddress(shippingAddress, isThai)
        : undefined;

      const single1PItem = basket?.basketItems?.find(
        (item) =>
          item.productDetails?.variants[0]?.storeCode && is1PMakroStore(item.productDetails?.variants[0]?.storeCode)
      );

      if (
        isAddressFlowRevampEnabled &&
        single1PItem &&
        single1PItem.productDetails.variants[0].storeCode !== currentStore?.storeCodes[0]
      ) {
        const storeInfo = await MultiStoreService.getStoreInfoByLocation(
          currentStore?.zipcode || '',
          currentStore?.subdistrict || '',
          isExpress
        );
        if (isSyncAddress) {
          dispatch(
            updateMetaDataAction({
              address: shippingAddress as UserAddress,
              storeInfo
            })
          );
        } else {
          await updateBasketStoreCodeREST(
            storeInfo.stores,
            currentStore?.zipcode || '',
            currentStore?.subdistrict || '',
            idToken || '',
            isExpress
          );
          dispatch(getBasketCalculationAction());
        }
        setCheckoutInitiated(false);
        return;
      }

      const basketItemPayload = basket?.basketItems
        .map((itm: any) => {
          if (persistedSelectedItems?.includes(itm.productId)) {
            return {
              productId: itm.productId,
              quantity: itm.quantity
            };
          }
          return undefined; // Explicitly return undefined if the item isn't selected
        })
        .filter((item): item is BasketItemInput => Boolean(item));

      let checkoutId;
      let isFlexibleBasket = false;
      if (persistedSelectedItems?.length && customerDefaultAddress && basketItemPayload?.length) {
        const sanitizedAddress = sanitizeAddress(customerDefaultAddress);
        const checkoutPayload = createCheckoutPayload(
          'direct',
          undefined,
          sanitizedAddress.zip as string,
          sanitizedAddress?.subdistrict as string,
          sanitizedAddress,
          i18n.language,
          true,
          true,
          basket?.basketItems?.length || 0,
          basket?.prices?.totalFinalPriceToPay || 0,
          accessTradeClickId,
          pricezaClickId,
          isExpress,
          appVersion,
          deviceOS,
          defaultMembershipCard?.id,
          trueId,
          appMode,
          mpid
        );

        const checkout = await createPartialCheckoutV3(checkoutPayload, basketItemPayload, idToken || '');
        checkoutId = checkout?.id;
        isFlexibleBasket = true;
      }

      if (!checkoutId) {
        setCheckoutInitiated(false);
        return;
      }
      await addCheckoutStartAnalytics(checkoutId, isFlexibleBasket);
      SendBrowserEvent.goToCheckout(checkoutId);
      if (deviceOS !== 'ios') {
        localStorage.removeItem('hasShownTooltip');
      }
      setCheckoutInitiated(false);
    } catch (error: any) {
      setCheckoutInitiated(false);
      sentry.captureException(error);
      if (
        error?.message?.includes('Express slots not available') ||
        error?.msg?.includes('Express slots not available')
      ) {
        SendBrowserEvent.showErrorToast(undefined, t('express.message.slotIsFull'));
      } else {
        SendBrowserEvent.showErrorToast(undefined, t('message.somethingWentWrong'));
      }
      console.error('An error occurred during checkout:', error);
    }
  };

  const addCheckoutStartAnalytics = async (checkoutId?: string, isFlexibleBasket?: boolean) => {
    const analyticPayload: any = {
      itemsCount: isFlexibleBasket ? basket?.basketItemsFromFe?.length || 0 : basket?.basketItems?.length || 0,
      total: basket?.prices?.totalFinalPriceToPay || basket?.total || 0,
      voucher: basket?.loyaltyPoints ? '' : basket?.voucherId || '',
      loyaltyPoints: basket?.loyaltyPoints || 0,
      estimatedPoints: basket?.estimatedPointsReceive || 0,
      cart_id: basket?.id || '',
      basketId: basket?.id || '',
      checkout_id: checkoutId || '',
      checkoutId: checkoutId || '',
      delivery: basket?.prices?.shippingFee || 0,
      productIds: isFlexibleBasket
        ? basket?.basketItemsFromFe || []
        : basket?.basketItems?.map((item: any) => item.productId) || [],
      priceBeforeDiscount: basket?.prices?.totalOriginalPrice || '',
      discount: basket?.prices?.roundedDiscountAmount || '',
      voucherAmount: basket?.prices?.exactVoucherDiscountAmount || '',
      shippingAddress: (shippingAddress as any) || '',
      feType: 'SAC',
      channel: SUGGESTEDLIST_CHANNELS.MKP_WEB,
      fromExpress: isExpress,
      storeId: basket?.storeCode || '',
      segment_source_name: isReactNativeWebView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
      basketSize: basket?.basketItems?.length || 0,
      onePCount: filter1PItems(basket?.basketItems || []).length || 0,
      threePCount: filter3PItems(basket?.basketItems || []).length || 0,
      flexSize: persistedSelectedItems?.length,
      isFromBasketNotification
    };
    if (pricezaClickId) {
      analyticPayload.clickId = pricezaClickId;
      analyticPayload.clickIdSource = AffiliateProgramType.PRICEZA;
    }
    if (accessTradeClickId) {
      analyticPayload.clickId = accessTradeClickId;
      analyticPayload.clickIdSource = AffiliateProgramType.ACCESSTRADE;
    }
    await analytics.trackEvent(ANALYTICS.CHECKOUT_START, analyticPayload);
  };

  const listItemsToBeAdjustedDueToInsufficientInventory = (basket: BasketState) => {
    const itemsToBeAdjusted: ItemToUpdateQuantity[] = [];
    if (!basket) return [];
    const basketItems = basket.basketItems || [];
    for (const item of basketItems) {
      const totalInventory = item.productDetails.totalInventory || 0;
      if (item.quantity > totalInventory && totalInventory > 0)
        itemsToBeAdjusted.push({
          variantId: item.variantId,
          quantity: item.productDetails.totalInventory
        });
    }
    return itemsToBeAdjusted;
  };

  const ensureItemsQuantityInBasketMatchWithInventory = async () => {
    if (!basket) return;
    const itemsToUpdate = listItemsToBeAdjustedDueToInsufficientInventory(basket);
    if (itemsToUpdate.length > 0) {
      const quantityChangedProductHash: IBasketQuantityChangedProductMap = {};
      itemsToUpdate.forEach((item) => {
        const product = basket.basketItems.find((product) => product.variantId === item.variantId);
        quantityChangedProductHash[item.variantId] = {
          currentQuantity: item.quantity,
          previousQuantity: product?.quantity
        };
      });
      dispatch(setBasketQuantityChangedProductMapAction(quantityChangedProductHash));
      dispatch(batchUpdateItemsQuantityAction(itemsToUpdate));
      SendBrowserEvent.showInfoToast(t('message.basketGotAffectedDueToAddressChange'));

      //set the flag of the items that were adjusted thier quantity to highllight to the user in the basket
      dispatch(setVariantIdsOfItemThatWasAdjustedQuantityAction(itemsToUpdate.map((item) => item.variantId)));
    } else {
      const variantIDs = Object.keys(basketQuantityChangedProductMap);
      const basketItems = basket.basketItems || [];
      const basketVariantIDs = basketItems.map((item) => item.variantId);
      const quantityChangedVariantIds = variantIDs.filter((id) => basketVariantIDs.includes(id));

      if (variantIDs.length > 0 && quantityChangedVariantIds.length === 0) {
        dispatch(setBasketQuantityChangedProductMapAction({}));
      }
    }
  };

  const renderAffectedBasketProduct = (itm: BasketLineItem, key: number, isUnavailable?: boolean) => {
    let originPrice = itm.productDetails.originPrice;
    let displayPrice = itm.productDetails.displayPrice;
    const [variant] = itm.productDetails.variants;
    originPrice = parseFloat(variant.compareAtPrice);
    if (itm.promotedPrice) {
      displayPrice = itm.promotedPrice;
    } else {
      displayPrice = variant.price;
    }

    const {status: productStatus, isAvailable} = getProductAvailability(itm.productDetails, currentStoreCodes || [], {
      alcoholExpiryDate: defaultMembershipCard?.alcoholExpiryDate
    });
    const isItemOnProhibitedPeriod =
      itm.productDetails.unavailableReason?.code === ProductUnavailableReasonCodes.ALCOHOL_PROHIBITED_PERIOD;

    return (
      <StyledProductInfoWrapper key={itm.id} isUnavailable={!!isUnavailable}>
        <ProductItem
          isProductUnavailable={!isAvailable || isItemOnProhibitedPeriod}
          productUnavailableText={isItemOnProhibitedPeriod ? t(`label.restricted`) : t(`label.${productStatus}`)}
          title={itm.productDetails.title}
          coverImageURL={itm.productDetails.imageUrls[0]}
          originPrice={originPrice}
          displayPrice={displayPrice}
          priceUnit={itm.productDetails.priceUnit}
          quantity={itm.quantity}
          weight={itm.productDetails?.size || ''}
          productURL={`/p/${itm.productId}`}
          maxQuantity={itm.productDetails.totalInventory}
          promotedPrice={itm.productDetails.promotedPrice}
          basketItem={itm}
        />
        {basket && basket?.basketItems?.length - 1 === key ? null : <Divider />}
      </StyledProductInfoWrapper>
    );
  };

  const processPostCheckoutClick = () => {
    const validateCheckout = (newlyFetchedItems: BasketLineItem[]) => {
      const newUnavailableItems = filterUnavailableProducts(newlyFetchedItems, currentStoreCodes || []);
      if (newUnavailableItems.length > 0) {
        SendBrowserEvent.showErrorToast(undefined, t('message.productsAreOutOfStock'));
        return false;
      }
      return true;
    };
    return handleCheckout(validateCheckout);
  };

  const newOnCheckoutClick = async (isOOSProductsDeleted = false) => {
    const hasUnavailableProducts = unavailableProducts.length > 0;
    if (!isOOSProductsDeleted) {
      const isModalShown = hasUnavailableProducts;
      if (isModalShown) {
        setBasketAffectedModalData({
          ...basketAffectedModalData,
          modalShown: isModalShown,
          notReadyToPurchase: unavailableProducts.length,
          quantityUpdatedItems: 0
        });
        return;
      }
    } else {
      setBasketAffectedModalData({
        ...basketAffectedModalData,
        modalShown: false
      });
    }

    processPostCheckoutClick();
  };

  // const selectedItemsOptimized = selectedItems;
  const selectedItemsOptimized = useOptimizedDependency(selectedItems, (value) => [...(value || [])].sort().join('-'));
  const basketItemsOptimized = useOptimizedDependency(basket?.basketItems, (value) =>
    [...(value || [])]
      .map((record) => record.productId)
      .sort()
      .join('-')
  );

  useEffect(() => {
    if (
      !omniChannelsVoucherRedemptionEnabled ||
      // async load requirement
      !basket?.storeCode ||
      // if no item in the basket, no need to fetch voucher
      !selectedItemsOptimized?.length ||
      !basketItemsOptimized?.length ||
      // currently only for mobile app
      !isOmniVoucherPlatformEnabled ||
      !defaultMembershipCard
    ) {
      return;
    }
    const query: FetchBasketVouchersQuery = {
      lang: isThai ? 'th' : 'en',
      storeId: basket.storeCode,
      itemDetails: basketItemsOptimized.map((record) => ({
        itemCode: record.productDetails.sku,
        selected: selectedItemsOptimized?.includes(record.productId) || false
      }))
    };
    dispatch(fetchBasketVouchersAction(query));
  }, [
    defaultMembershipCard,
    omniChannelsVoucherRedemptionEnabled,
    basketItemsOptimized,
    dispatch,
    idToken,
    isThai,
    selectedItemsOptimized,
    isOmniVoucherPlatformEnabled,
    basket?.storeCode
  ]);

  const handleConfirmAndContinue = async () => {
    try {
      dispatch(removeUnavailableItemsAction(newOnCheckoutClick));
    } catch (error: any) {
      SendBrowserEvent.showErrorToast(undefined, error.message || t('global.message.somethingWentWrong'));
    } finally {
      setBasketAffectedModalData({
        modalShown: false,
        notReadyToPurchase: 0,
        quantityUpdatedItems: 0
      });
    }
  };

  const editAddress = async (address: UserAddress) => {
    setAddressBeingEdited(address);

    let selectedAddressLat = address?.locus_latitude || address?.user_latitude;
    let selectedAddressLng = address?.locus_longitude || address?.user_longitude;

    if (!selectedAddressLat && !selectedAddressLng) {
      const addressString = getAddressStringForGeocoding(address);
      if (addressString) {
        try {
          await loadGoogleMapsApi(config.GOOGLE_MAP_API_KEY);
          const selectedAddressCoords = await geocodeAddress(addressString, address.zip);
          if (selectedAddressCoords) {
            selectedAddressLat = selectedAddressCoords.lat();
            selectedAddressLng = selectedAddressCoords.lng();
          }
        } catch (err) {
          console.error('Error during geocoding:', err);
        }
      }
    }

    setPinMapData({
      ...pinMapData,
      ...(selectedAddressLat && selectedAddressLng
        ? {coordinates: {lat: selectedAddressLat, lng: selectedAddressLng}}
        : {}),
      zoomLevel: Number(address.metadata?.zoomLevel) || 18,
      address: address.address1,
      postalCode: address.zip,
      district: isThai ? address.district?.nameTh : address.district?.nameEn,
      province: isThai ? address.province?.nameTh : address.province?.nameEn,
      subdistrict: isThai ? address.subDistrict?.nameTh : address.subDistrict?.nameEn,
      subdistrictEn: address.subDistrict?.nameEn
    });

    _showPinMapModal();
  };

  const onCheckoutClick = async () => {
    if (appliedOmniChannelVoucher) {
      const appliedOmniChannelVoucherExpiry = appliedOmniChannelVoucher?.expiryDate
        ? new Date(appliedOmniChannelVoucher.expiryDate)
        : null;
      if (appliedOmniChannelVoucherExpiry && appliedOmniChannelVoucherExpiry < new Date()) {
        dispatch(
          setAlertAction({
            type: AlertType.voucherExpired,
            details: {
              needTriggerCheckoutOnRetry: true
            }
          })
        );
        return;
      }
      try {
        await applyPromotionVoucher(idToken, appliedOmniChannelVoucher.voucherId, isExpress);
      } catch (err) {
        dispatch(
          setAlertAction({
            type: AlertType.voucherExpired,
            details: {
              needTriggerCheckoutOnRetry: true
            }
          })
        );
        return;
      }
    }

    if (!shippingAddress?.id) {
      if (userAddressList?.length) {
        toggleAddressDrawer();
        SendBrowserEvent.showInfoToastEnabled(t('message.selectShippingAddress'));
        return;
      } else {
        _onNewAddress();
        SendBrowserEvent.showInfoToastEnabled(t('message.completeShippingAddress'));
        return;
      }
    } else if (!shippingAddress?.user_latitude || !shippingAddress?.user_longitude) {
      setAddressBeingEdited(shippingAddress);
      _showPinMapModal();
      return;
    }
    if (!(await MultiStoreService.isLocationDeliverable(shippingAddress, isExpress))) {
      SendBrowserEvent.showErrorToast(undefined, t('message.undeliverableAddressLocation'));
      return;
    }
    if (getAlcoholProducts(basket?.basketItems as BasketLineItem[]).length) {
      const selectedAlcoholBasketItems = basket?.basketItems?.filter(
        (item) => persistedSelectedItems?.includes(item.productId) && item.productDetails.isAlcohol
      ) as BasketLineItem[];
      const isSufficientAlcoVolume =
        getAlcoholVolumeInLiters(selectedAlcoholBasketItems) >=
        (basket?.alcoholLitersThreshold || ALCOHOL_VOLUME_THRESHOLD);
      if (!isSufficientAlcoVolume && selectedAlcoholBasketItems.length) {
        SendBrowserEvent.showErrorToast(
          undefined,
          t('alcohol.message.addMoreSelectedAlcohol', {
            amount: basket?.alcoholLitersThreshold || ALCOHOL_VOLUME_THRESHOLD
          })
        );
        return;
      }
    }
    newOnCheckoutClick(true);
  };

  useEffect(() => {
    if (!needRetriggerCheckout) {
      return;
    }
    setNeedRetriggerCheckoutAction(false);
    onCheckoutClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needRetriggerCheckout]);

  useEffect(() => {
    if (userAddressList.length && currentStore) {
      dispatch(updateBasketAddressAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAddressList, currentStore]);

  useEffect(() => {
    if (isCurrentStoreLoaded && !isAppLoading && hasIdToken) {
      dispatch(
        getBasketCalculationAction({
          isSelectAllItems: !selectedItemsLocalStorage.exist() || selectedItemsLocalStorage.get().length === 0
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultMembershipCard, isAppLoading, hasIdToken, isCurrentStoreLoaded, dispatch, appMode]);

  useEffect(() => {
    if (isAppLoading || !defaultMembershipCard) {
      return;
    }
    dispatch(fetchUserPointsAction());
  }, [defaultMembershipCard, isAppLoading, dispatch]);

  useEffect(() => {
    if (currentStore && !isAppLoading && idToken && customerId && !isEnabledRecommendationMFE) {
      dispatch(getBasketRecommendationsAction());
    }
  }, [defaultMembershipCard, isAppLoading, currentStore, dispatch, idToken, customerId, appMode]);

  useEffect(() => {
    if (idToken && config.IS_ADDRESS_SECTIONS_ENABLED) {
      dispatch(fetchUserDetailsAction());
    }
  }, [dispatch, idToken]);

  useEffect(() => {
    if (isDirectCheckout) toggleAddressDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirectCheckout]);

  useEffect(() => {
    ensureItemsQuantityInBasketMatchWithInventory();
    if (basket?.basketItems.length && !persistedSelectedItems) {
      const listOfProductIds = getIdsFromBasketItems(basket?.basketItems || []) || [];
      dispatch(setSelectedItemsAction([...listOfProductIds]));
      selectedItemsLocalStorage.set(listOfProductIds);
    } else if (basket?.basketItems.length && persistedSelectedItems?.length && !selectedItems?.length) {
      dispatch(setSelectedItemsAction([...persistedSelectedItems]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basket]);

  useEffect(() => {
    if (!isWithinWeightThreshold && isExpress && weightThreshold && !!numberOfItems) {
      SendBrowserEvent.showErrorToast(undefined, t('express.message.basketIsFull', {threshold: weightThreshold}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWithinWeightThreshold, isExpress, weightThreshold, numberOfItems]);

  useEffect(() => {
    if (!isWithinDimensionThreshold && isExpress && dimensionThreshold && !!numberOfItems) {
      SendBrowserEvent.showErrorToast(
        undefined,
        t('express.message.basketDimensionExceeded', {threshold: dimensionThreshold})
      );
    }
  }, [isWithinDimensionThreshold, isExpress, dimensionThreshold, numberOfItems, t]);

  const isBasketLoading =
    (!(basket && Object.keys(basket).length > 0) && !isBasketApiFailed && !!idToken && !isStoreInActive) ||
    isLoading ||
    isAppLoading;

  const updateStoresForBasket = async (address: UserAddress) => {
    const storeInfo = await MultiStoreService.getStoreInfoByLocation(
      address.zip,
      address.subDistrict?.nameEn || '',
      isExpress,
      isUnifiedBasketEnabledFlag
    );
    if (isSyncAddress) {
      dispatch(updateMetaDataAction({address, storeInfo}));
      return;
    }
    const basketStore = await updateBasketStoreCodeREST(
      storeInfo.stores,
      address.zip,
      address?.subDistrict?.nameEn || '',
      idToken,
      isExpress
    );
    if (basketStore?.status === 'SUCCESS') {
      const showStorePickup =
        storeInfo?.stores?.[0].warehouseStoreData?.storeConfigurations?.pickupAtStoreEnable || false;
      const storeCodes = storeInfo?.stores?.map((store) => store.storeCode) ?? [];
      const expressStoreCodes = storeInfo?.expressStores?.map((store) => store.storeCode) ?? [];
      const currentStorePayload = {
        zipcode: storeInfo?.zipcode,
        subdistrict: storeInfo.subdistrict,
        storeCodes,
        showStorePickup,
        isSameDayDelivery: storeInfo?.stores[0]?.sameDay,
        expressStoreCodes
      };
      dispatch(setCurrentStoreWithLocationLocalesAction(currentStorePayload));
      if (isExpress && !expressStoreCodes.length && config.ENABLE_UNIFY_BASKET) {
        setAppModeAction(AppMode.regular);
      }
      // if the zipcode and subdistrict changed, prevent this as the MFE will be reloaded
      // by the host so we dont have double calculate call
      // zipcode & subdistrict changed = let host trigger reload
      // zipcode & subdistrict unchanged = internal MFE refresh
      const isZipcodeOrSubdistrictChanged =
        shippingAddress?.zip !== address.zip || shippingAddress?.subDistrict?.id !== address.subDistrict?.id;
      if (!isZipcodeOrSubdistrictChanged) {
        dispatch(getBasketCalculationAction());
      }
    }
  };

  const onClickSelectAllItems = (e: any) => {
    const checked = e.target.checked;
    const allItemsIds = basket?.basketItems.map((item) => item.productId) || [];
    if (checked) {
      dispatch(selectItemIdsAction(allItemsIds));
    } else {
      dispatch(checkedUnselectItemIdsAction(allItemsIds));
    }
  };

  const _onPinMapSelect = (newPinMapData: TPinMapSelectedLocation) => {
    if (newPinMapData?.coordinates) {
      setPinMapData(newPinMapData);

      setIsAddressFormModalOpen(true);
    } else {
      setIsPinMapViewOpen(false);
      setHasUserMovedMapManually(false);
      //close address list as well if no address is selected
      // if (!addressBeingEdited?.id) {
      //   // if new address, redirect back to basket view
      //   setAddressDrawerOpen(false);
      // }
    }
  };

  const _onSaveAddress = (open: boolean, isSaved = false) => {
    setIsAddressFormModalOpen(open);

    if (isSaved) {
      setIsPinMapViewOpen(false);
      setHasUserMovedMapManually(false);
    }
  };

  const addAddressEvent = (eventName: string, payload: any) => {
    analytics.trackEvent(eventName, {
      zip: currentStore?.zipcode,
      sub: currentStore?.subdistrict || '',
      addressesTotal: userAddressList?.length,
      storeId: basket?.storeCode || '',
      channel: SUGGESTEDLIST_CHANNELS.MAKRO_PRO,
      segment_source_name: isReactNativeWebView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
      basketSize: basket?.basketItems?.length || 0,
      onePCount: filter1PItems(basket?.basketItems || []).length || 0,
      threePCount: filter3PItems(basket?.basketItems || []).length || 0,
      ...payload
    });
  };

  const _showPinMapModal = () => {
    setIsPinMapViewOpen(true);
    addAddressEvent(ANALYTICS.ADDRESS_LOCATION_PROMPT, {});
  };

  const _onNewAddress = () => {
    setPinMapData(null);
    setAddressBeingEdited(undefined);
    _showPinMapModal();
  };

  return (
    <StyledBasket open={true} transitionDuration={0} variant="persistent">
      <BuildInfo />
      <Header
        address={shippingAddress as UserAddress}
        isLoadingAddress={checkoutInitiated || isBasketLoading}
        onShipToClick={() => onShipToClickHandle()}
        title={isExpress && !isUnifiedBasketEnabledFlag ? t('express.title.expressBasket') : t('title')}
        setShowConfirmModal={setShowConfirmModal}
      />
      <Body
        loading={isBasketLoading}
        address={shippingAddress as UserAddress}
        isLoading={checkoutInitiated}
        onClick={() => onShipToClickHandle()}
      />
      <Footer
        shouldShowMinBasketWarning={has1PSelected}
        onCheckoutClick={onCheckoutClick}
        isCheckoutDisabled={isDisabledCheckoutBtn()}
        loading={checkoutInitiated || isBasketLoading}
        onClickSelectAllItems={onClickSelectAllItems}
        setShowBasketSummaryModal={setShowBasketSummaryModal}
        showBasketSummaryModal={showBasketSummaryModal}
      />
      <AddressDrawer
        title={t('label.shippingAddress')}
        open={isAddressDrawerOpen}
        toggleAddressDrawer={toggleAddressDrawer}
        onEditAddress={editAddress}
        onNewAddress={_onNewAddress}
        shippingAddress={shippingAddress}
        updateStoresForBasket={updateStoresForBasket}
      />
      {isAddressFormModalOpen ? (
        <AddressFormView
          modalOpen={isAddressFormModalOpen}
          setModalOpen={_onSaveAddress}
          address={addressBeingEdited}
          initialPinMapData={pinMapData}
          onFormSubmit={() => {
            setAddressDrawerOpen(false);
          }}
          openFullMapView={openFullMapView}
          setOpenFullMapView={setOpenFullMapView}
          updateStoresForBasket={updateStoresForBasket}
          hasUserMovedMapManually={hasUserMovedMapManually}
          setAddressBeingEdited={setAddressBeingEdited}
        />
      ) : null}
      {isPinMapViewOpen ? (
        <PinMapModal
          initialPinMapData={pinMapData}
          open={isPinMapViewOpen}
          isAddressFormOpen={isAddressFormModalOpen}
          onSave={_onPinMapSelect}
          addAddressEvent={addAddressEvent}
          setHasUserMovedMapManually={setHasUserMovedMapManually}
        />
      ) : null}

      <BasketPopUpDialog
        open={basketAffectedModalData.modalShown}
        title={t('label.basketUpdate')}
        modalInfo={t('message.notAvailableProductsWillBeRemoved')}
        confrmButtonText={t('button.confirm&continue')}
        onGoBack={() => {
          setBasketAffectedModalData({
            ...basketAffectedModalData,
            modalShown: false
          });
        }}
        loading={checkoutInitiated}
        onConfirmAndContinue={handleConfirmAndContinue}
      >
        {unavailableProducts.length && (
          <Box mt={1}>
            <Typography fontSize={16} fontWeight={700} ml={-0.5}>
              {t('label.remove')}
            </Typography>
            <StyledSectionWrap className={`${unavailableProducts.length > 1 ? 'multiple' : 'single'}`}>
              <StyledSectionContentContainer>
                {unavailableProducts.map((itm, key) => {
                  return renderAffectedBasketProduct(itm, key, true);
                })}
              </StyledSectionContentContainer>
            </StyledSectionWrap>
          </Box>
        )}
      </BasketPopUpDialog>

      <FlexiBasketConfirmModal
        onConfirm={() => {
          dispatch(deleteSelectedItemsAction());
          setShowConfirmModal(false);
        }}
        setShowConfirmModal={setShowConfirmModal}
        open={showConfirmModal}
        message={t('flexiBasket.deleteModalInfoText', {
          numberOfItems
        })}
        isReactNativeWebView={isReactNativeWebView}
      />
      <BasketSummaryModal
        showBasketSummaryModal={showBasketSummaryModal}
        setShowBasketSummaryModal={setShowBasketSummaryModal}
        onCheckoutClick={onCheckoutClick}
        isCheckoutDisabled={isDisabledCheckoutBtn()}
        loading={checkoutInitiated || isBasketLoading}
        onClickSelectAllItems={onClickSelectAllItems}
      />
      <ResponsiveAlertPrompts />
    </StyledBasket>
  );
};

export default BasketWrapper;
